import { Component } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { ApiKeyDialogComponent } from '../../components/dialogs/api-key-dialog/api-key-dialog.component';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { DomainWhitelistDialogComponent } from '../../components/dialogs/domain-whitelist-dialog/domain-whitelist-dialog.component';
import { EmbeddingDialogComponent } from '../../components/dialogs/embedding-dialog/embedding-dialog.component';
import { copyToClipboard } from '../../helpers/copyToClipboard';
import { formatNumber } from '../../helpers/formatNumbers';
import { AuthService } from '../../services/auth.service';
import { CreditPricing, CreditService } from '../../services/credit.service';
import { canSeeCreditAnalytics } from '../credit-analytics/credit-analytics.access';
import { OrganisationService } from '../../services/organisation.service';
import { ApiKey, UsageService } from '../../services/usage.service';

@Component({
  selector: 'app-usage',
  templateUrl: './usage.component.html',
  styleUrl: './usage.component.scss',
})
export class UsageComponent {
  constructor(
    protected dialogService: NbDialogService,
    protected usageService: UsageService,
    protected organisationService: OrganisationService,
    protected authService: AuthService,
    protected nbToastrService: NbToastrService,
    protected translate: TranslateService,
    protected creditService: CreditService
  ) {
    this.refresh();
  }

  /** The token view (tile + per-key bar) is kept as a backup and switched off
   *  in the UI: customers see credits, tokens are an internal unit (Lasse
   *  2026-10-05). Set to true to bring the old view back. */
  readonly showLegacyTokenUsage = false;

  creditPricing: CreditPricing | null = null;

  refresh() {
    this.usageService.listApiKeys().subscribe();
    this.organisationService.getOrganisation().subscribe();
    this.creditService.getBalance().subscribe({ error: () => undefined });
    this.creditService.getPricing().subscribe({
      next: (pricing) => (this.creditPricing = pricing),
      error: () => (this.creditPricing = null),
    });
  }

  // ─── Credits at a glance ────────────────────────────────────────────────────

  /** Monthly plan allowance in credits; null = the plan sets no credit budget. */
  get planCreditsIncluded(): number | null {
    return this.creditPricing?.plan.included_credits ?? null;
  }

  get planCreditsUsed(): number {
    return this.creditPricing?.plan.used_credits ?? 0;
  }

  get planCreditsRemaining(): number | null {
    return this.creditPricing?.plan.remaining_credits ?? null;
  }

  get purchasedCredits(): number {
    return this.creditService.balance$.value?.credits_remaining ?? 0;
  }

  /** Tile colour like the other quotas: by the share of plan credits used. */
  get planCreditsStatus(): string {
    const included = this.planCreditsIncluded;
    return included ? this.getStatus(this.planCreditsUsed, included) : 'success';
  }

  /** Purchased credits do not lapse with the period, but each purchase has
   *  an expiry date — name the next one when there is one. */
  get purchasedCreditsTip(): string {
    const expires = this.creditService.balance$.value?.credits_oldest_expires_at;
    const base = this.translate.instant('pages.usage.credits.purchasedTip');
    if (!expires) return base;
    return (
      base +
      ' ' +
      this.translate.instant('pages.usage.credits.purchasedTipExpiry', {
        date: new Date(expires).toLocaleDateString(this.translate.currentLang || 'de-DE'),
      })
    );
  }

  get canSeeCreditDetails(): boolean {
    return canSeeCreditAnalytics(this.authService.accessTokenPayload$.value?.role);
  }

  getUsagePeriod() {
    const start =
      this.organisationService.organisation$.value?.cycle_start_date;
    const end = this.organisationService.organisation$.value?.cycle_end_date;

    if (start && end) {
      return this.translate.instant('pages.usage.timeIntervalFromTo', {
        from: new Date(start).toLocaleDateString(
          this.translate.currentLang || 'de-DE'
        ),
        to: new Date(end).toLocaleDateString(
          this.translate.currentLang || 'de-DE'
        ),
      });
    }
    return '';
  }

  // Add API Key logic
  addApiKey() {
    const dialogRef = this.dialogService.open(ApiKeyDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
    });
  }

  get apiUsage() {
    return this.usageService.usage$.value.api_usage;
  }

  editWhitelist() {
    const dialogRef = this.dialogService.open(DomainWhitelistDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
    });
  }

  copyApiKey(key: ApiKey) {
    copyToClipboard(key.clear_api_key);
    this.nbToastrService.success(
      'API-Schlüssel wurde in die Zwischenablage kopiert.',
      'Kopiert!'
    );
  }

  // Delete API Key logic
  deleteApiKey(key: ApiKey) {
    const confirmDialogRef = this.dialogService.open(ConfirmDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
    });

    // Delete the project if the user confirms
    confirmDialogRef.onClose.subscribe(async (isConfirmed: boolean) => {
      if (isConfirmed) {
        this.usageService.deleteApiKey(key.id).subscribe();
      }
    });
  }

  get usage() {
    return this.usageService.usage$.value;
  }

  get moodleCourseUsage() {
    return this.usage?.moodle_course_usage ?? { current_count: 0, quota_limit: 0 };
  }

  get apiKeys() {
    return this.usageService.apiKeys$.value;
  }

  getStatus(freeAmount: number = 0, maxAmount: number = 100) {
    const value = this.getPercentage(freeAmount, maxAmount);
    if (value < 70) {
      return 'success';
    } else if (value < 90) {
      return 'warning';
    } else {
      return 'danger';
    }
  }

  getProgress(freeAmount: number, maxAmount: number) {
    let progress = this.getPercentage(maxAmount - freeAmount, maxAmount);

    // round to 2 decimal places
    progress = parseFloat(progress.toFixed(2));

    return progress < 1 ? (freeAmount === 0 ? 0 : 1) : progress;
  }

  getPercentage(value: number = 0, max: number = 1) {
    return ((value || 0) / (max || 1)) * 100;
  }

  showEmbedding(a: ApiKey) {
    this.dialogService.open(EmbeddingDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
      context: {
        apiKey: a.clear_api_key,
      },
    });
  }

  getMegaBytes(bytes: number = 0) {
    return parseFloat((bytes / 1024 / 1024).toFixed(2));
  }

  formatNumber(num: number = 0) {
    return formatNumber(num);
  }
}
